/* eslint-disable consistent-return */
import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import DtoPiezometerLight from 'piezometry/dto/DtoPiezometerLight'
import MessageCard from 'components/card/MessageCard'
import i18n from 'simple-react-i18n'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { intParser } from 'utils/customHook/useApplicationSetting'
import { flatten, isNil, orderBy, uniqBy } from 'lodash'
import { getBeginingOfTheYear, getDate, getEndOfTheYear } from 'utils/DateUtil'
import PiezometerStationAction from 'station/actions/PiezometerStationAction'
import useProgressDispatch from 'utils/customHook/useProgressDispatch'
import { MEASURE_COTE } from 'piezometry/constants/PiezometryConstants'
import { exportFile } from 'utils/ExportDataUtil'
import PiezometryAction from 'piezometry/actions/PiezometryAction'
import Checkbox from 'components/forms/Checkbox'
import { Card, CardContent, Grid2, Popover } from '@mui/material'
import useBoolean from 'utils/customHook/useBoolean'
import useStartPeriod from 'catchment/utils/useStartPeriod'
import useEndPeriod from 'catchment/utils/useEndPeriod'
import { getStationName } from 'catchment/utils/CatchmentUtil'
import { CardTitle } from 'components/card/NewCard'
import { PiezoOptionIcon } from './PiezoOption'
import CircularProgressWithLabel from 'components/progress/CircularProgressWithLabel'
import PiezoChart from './PiezoChart'
import useLocalStorage from 'utils/customHook/useLocalStorage'
import SuperMultiAutocomplete from 'components/forms/SuperMultiAutocomplete'

const Chip = forwardRef(({
    label = '',
    color = '',
    onClick,
    disabled = false,
    style = {},
}, ref) => (
    <span
        ref={ref}
        onClick={() => !disabled && onClick?.()}
        style={{
            cursor: disabled ? 'default' : 'pointer',
            backgroundColor: disabled ? '#AAAAAA' : color,
            color: 'white',
            padding: '3px 6px',
            borderRadius: '10px',
            ...style,
        }}
    >
        {label}
    </span>
))

Chip.propTypes = {
    label: PropTypes.string,
    color: PropTypes.string,
    onClick: PropTypes.func,
    disabled: PropTypes.bool,
    style: PropTypes.object,
}

const getCote = cote => {
    switch (cote) {
        case MEASURE_COTE.DEPTH: return { label: 'Profondeur / Dernier repère', color: '#2196F3' }
        case MEASURE_COTE.NGF: return { label: 'NGF', color: '#283593' }
        case MEASURE_COTE.GROUND: return { label: 'Sol', color: '#ff9800' }
        case MEASURE_COTE.LANDMARK: return { label: 'Historique des repères', color: '#6d4c41' }
        default: return { label: 'Profondeur / Dernier repère', color: '#2196F3' }
    }
}

const PiezoOptionChip = ({
    defaultCote,
    onChangeCote = () => {},
    disabled = false,
}) => {
    const {
        value: isOpen,
        toggle: toggleOpen,
    } = useBoolean(false)
    const anchorEl = useRef()

    const selectedCote = useMemo(() => getCote(defaultCote), [defaultCote])

    const onChange = (cote) => {
        onChangeCote(cote)
        toggleOpen()
    }

    return (
        <>
            <Chip
                label={selectedCote.label}
                color={selectedCote.color}
                disabled={disabled}
                onClick={toggleOpen}
                ref={anchorEl}
            />
            <Popover
                open={isOpen}
                anchorEl={anchorEl.current}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                onClose={toggleOpen}
            >
                <Grid2 container direction='column' style={{ padding: '5' }} spacing={0.5}>
                    <Grid2 display='flex' alignItems='center' style={{ cursor: 'pointer' }} onClick={() => onChange(MEASURE_COTE.DEPTH)}>
                        <Checkbox checked={defaultCote === MEASURE_COTE.DEPTH} />
                        <Chip label={'Profondeur / Dernier repère'} color='#2196F3' style={{ padding: '0px 6px' }}/>
                    </Grid2>
                    <Grid2 display='flex' alignItems='center' style={{ cursor: 'pointer' }} onClick={() => onChange(MEASURE_COTE.NGF)}>
                        <Checkbox checked={defaultCote === MEASURE_COTE.NGF} />
                        <Chip label={'NGF'} color='#283593' style={{ padding: '0px 6px' }}/>
                    </Grid2>
                    <Grid2 display='flex' alignItems='center' style={{ cursor: 'pointer' }} onClick={() => onChange(MEASURE_COTE.GROUND)}>
                        <Checkbox checked={defaultCote === MEASURE_COTE.GROUND} />
                        <Chip label={'Sol'} color='#ff9800' style={{ padding: '0px 6px' }}/>
                    </Grid2>
                    <Grid2 display='flex' alignItems='center' style={{ cursor: 'pointer' }} onClick={() => onChange(MEASURE_COTE.LANDMARK)} >
                        <Checkbox checked={defaultCote === MEASURE_COTE.LANDMARK} />
                        <Chip label={'Historique des repères'} color='#6d4c41' style={{ padding: '0px 6px' }}/>
                    </Grid2>
                </Grid2>
            </Popover>
        </>
    )
}

PiezoOptionChip.propTypes = {
    defaultCote: PropTypes.oneOf([MEASURE_COTE.DEPTH, MEASURE_COTE.NGF, MEASURE_COTE.GROUND, MEASURE_COTE.LANDMARK]),
    onChangeCote: PropTypes.func,
    disabled: PropTypes.bool,
}

const CatchmentFollowUpPiezo = ({
    piezometersLinked = [],
}) => {
    const dispatch = useDispatch()

    const {
        catchment,
        piezometers,
        piezometryDataTypes,
        cities,
    } = useSelector(store => ({
        catchment: store.CatchmentReducer.catchment,
        piezometers: store.PiezometryReducer.piezometersLight,
        piezometryDataTypes: store.PiezometryReducer.piezometryDataTypes,
        cities: store.CityReducer.cities,
    }), shallowEqual)

    const startPeriod = useStartPeriod(intParser)
    const endPeriod = useEndPeriod(intParser)

    const startDate = getBeginingOfTheYear(startPeriod)
    const endDate = getEndOfTheYear(endPeriod)

    const [dataTypes, setDataTypes] = useLocalStorage('CATCHMENT_PIEZO_DATATYPES', [-1, -2]) // profondeur, prélèvent
    const [group, setGroup] = useLocalStorage('CATCHMENT_PIEZO_GROUP', 'auto')
    const [thresholds, setThresholds] = useState([])
    const [dataMeasures, setDataMeasures] = useState([])
    const [displayCote, setdisplayCote] = useState(MEASURE_COTE.DEPTH)

    const filteredDataTypes = useMemo(() => piezometryDataTypes.filter(dt => dt.showData || dataTypes.includes(dt.id)), [dataTypes, piezometryDataTypes])

    const getDefaultSelectPointId = () => {
        const mainPointFound = catchment.catchmentPoints?.find(({ mainPoint }) => mainPoint)
        const code = mainPointFound?.codeWithoutDesignation
        const idMainPoint = mainPointFound && piezometers.find(q => q.code === code)?.id
        const defaultPoint = idMainPoint ?? piezometersLinked[0]?.id
        return isNil(defaultPoint) ? [] : [defaultPoint]
    }

    const [stations, setStations] = useState(getDefaultSelectPointId)

    useEffect(() => {
        setStations(getDefaultSelectPointId)
    }, [catchment, piezometers])

    useEffect(() => {
        dispatch(PiezometryAction.fetchPiezometryDataTypes())
    }, [])

    useEffect(() => {
        if (stations.length === 0) {
            setThresholds([])
            setdisplayCote(MEASURE_COTE.NGF)
            return
        }
        Promise.all(stations.map(s => PiezometerStationAction.getPiezometerThresholds(s, displayCote)))
            .then(result => setThresholds(flatten(result)))
    }, [stations, displayCote, dispatch])

    const {
        isLoaded,
        progress,
    } = useProgressDispatch((cancelRef) => {
        setDataMeasures(prev => prev.filter(dm => dataTypes.includes(dm.dataType)))
        const listInputs = piezometersLinked.flatMap(p => {
            return dataTypes
                .filter(dt => !dataMeasures.some(dm => dm.stationId === p.id && dm.dataType === dt && dm.displayCote === (dt === -1 ? displayCote : undefined) && dm.groupFunc === (dt === -2 && 'SUM' || dt === -1 && (group === 'AUTO' ? 'MAX' : group) || 'MAX')))
                .map(dataType => ({
                    chartMode: true,
                    startDate: getBeginingOfTheYear(startPeriod),
                    endDate: getEndOfTheYear(endPeriod),
                    dataType,
                    groupFunc: dataType === -2 && 'SUM' || dataType === -1 && (group === 'AUTO' ? 'MAX' : group) || 'MAX',
                    displayCote: dataType === -1 ? displayCote : undefined,
                    stationId: p.id,
                }))
        })

        return listInputs.map(input => dispatch(PiezometerStationAction.getPiezoChartMeasures(input))
            .then(data => !cancelRef.current && setDataMeasures(prev => uniqBy([data, ...prev], d => `${d.stationId}#${d.dataType}`))))
    }, [displayCote, group, dataTypes])

    const dataMeasuresFiltered = useMemo(() => {
        const orderedDataMeasures = orderBy(dataMeasures, 'dataType')
        if (stations.length === 0) return orderedDataMeasures
        return orderedDataMeasures.filter(({ stationId }) => stations.includes(stationId))
    }, [dataMeasures, stations])

    const actions = [
        <PiezoOptionIcon
            key={0}
            defaultGroupType={group}
            onChangeGroupType={setGroup}
            disabled={!isLoaded}
        />,
        {
            icon: 'file_download',
            tooltip: i18n.export,
            onClick: () => {
                const exportData = dataMeasures.flatMap(d => {
                    const piezo = piezometers.find(({ id }) => id === d.stationId)
                    const dataType = piezometryDataTypes.find(h => h.id === d.dataType)
                    return d.measures.map(m => ({
                        code: piezo?.code,
                        date: { value: getDate(m.date), format: 'dd/MM/yyyy', cellType: 'date' },
                        value: { value: m.value, format: '0.00000', cellType: 'number' },
                        name: piezo?.name || d.stationId,
                        dataType: dataType?.label ?? i18n.unknown,
                        cote: isNil(d.displayCote) ? '' : getCote(d.displayCote).label,
                    }))
                })
                exportFile({
                    data: exportData.length ? [
                        {
                            ...exportData[0],
                            headers: ['code', 'name', 'dataType', 'date', 'value', 'cote'],
                        },
                        ...exportData.slice(1),
                    ] : [],
                    exportType: 'xlsx',
                    titleFile: i18n.dataPiezo,
                }, true)
            },
        },
    ]

    const formattedPiezometers = useMemo(() => {
        return piezometersLinked.map(piezo => {
            const city = piezo && cities.find(c => c.code === piezo.townCode)
            return {
                name: getStationName(piezo, city),
                id: piezo.id,
            }
        })
    }, [cities, piezometersLinked])

    return (
        <Card data-cy='follow_up_piezo'>
            <CardTitle title={i18n.piezometry} actions={actions} />
            <CardContent>
                <Grid2 container alignItems='center' spacing={2}>
                    <Grid2 size={4}>
                        <SuperMultiAutocomplete
                            label={i18n.piezometer}
                            options={formattedPiezometers}
                            values={stations}
                            onChange={setStations}
                            keyValue={'id'}
                            multiple
                            limit={2}
                        />
                    </Grid2>
                    <Grid2 size={4}>
                        <SuperMultiAutocomplete
                            label={i18n.dataTypes}
                            options={filteredDataTypes}
                            onChange={setDataTypes}
                            values={dataTypes}
                            keyValue='id'
                            multiple
                            limit={3}
                        />
                    </Grid2>
                    <Grid2 size={2}>
                        <PiezoOptionChip
                            defaultCote={displayCote}
                            onChangeCote={setdisplayCote}
                        />
                    </Grid2>
                </Grid2>
                <div style={{ position: 'relative' }}>
                    {!isLoaded && (<CircularProgressWithLabel value={progress} />) }
                    {dataMeasuresFiltered.length > 0 && (
                        <PiezoChart
                            dataMeasures={dataMeasuresFiltered}
                            thresholds={thresholds}
                            startDate={startDate}
                            endDate={endDate}
                        />
                    )}
                    {dataMeasuresFiltered.length === 0 && (<MessageCard>{i18n.noDataToDisplay}</MessageCard>)}
                </div>
            </CardContent>
        </Card>
    )
}

CatchmentFollowUpPiezo.propTypes = {
    piezometersLinked: PropTypes.arrayOf(PropTypes.instanceOf(DtoPiezometerLight)).isRequired,
}

export default CatchmentFollowUpPiezo